import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { MdChat, MdEmail, MdLocationOn } from 'react-icons/md';
import { FaGithub, FaInstagram, FaLinkedin } from 'react-icons/fa6';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageTitle = styled(motion.h1)`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: ${({ theme }) => theme.colors.secondary};
  position: relative;

  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 100px;
    height: 4px;
    background-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ContactLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled(motion.div)`
  background-color: ${({ theme }) => theme.colors.light};
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
`;

const ContactActions = styled(motion.div)`
  background-color: ${({ theme }) => theme.colors.light};
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InfoItem = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const InfoIcon = styled.div`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.primary};
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    display: block;
  }
`;

const InfoText = styled.div`
  h3 {
    font-size: 1.2rem;
    margin-bottom: 0.5rem;
    color: ${({ theme }) => theme.colors.secondary};
  }

  p {
    color: ${({ theme }) => theme.colors.text};
    line-height: 1.6;
  }

  a {
    color: ${({ theme }) => theme.colors.text};
    text-decoration: none;
    transition: color 0.3s;

    &:hover {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  margin-top: 0.5rem;
  width: 100%;
`;

const SocialLinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.9rem 1rem;
  border: 1px solid rgba(252, 232, 232, 0.22);
  background-color: rgba(23, 3, 3, 0.72);
  color: ${({ theme }) => theme.colors.text};
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s;
  gap: 5px;

  svg {
    width: 30px;
    height: 30px;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ActionButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  text-decoration: none;
  border: none;
  border-radius: 8px;
  padding: 0.9rem 1.2rem;
  font-size: 1rem;
  font-weight: 600;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text};
  transition: background-color 0.25s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: 'beforeChildren',
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 }
  }
};

const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  const mailSubject = encodeURIComponent(String(t('contact.mailSubject')));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <PageContainer as={motion.div} variants={containerVariants} initial="hidden" animate="visible">
      <PageTitle variants={itemVariants}>{t('contact.title')}</PageTitle>

      <ContactLayout>
        <ContactInfo variants={itemVariants}>
          <InfoItem>
            <InfoIcon>
              <MdLocationOn size={28} aria-hidden />
            </InfoIcon>
            <InfoText>
              <h3>{t('contact.location.title')}</h3>
              <p>{t('contact.location.value')}</p>
            </InfoText>
          </InfoItem>

          <InfoItem>
            <InfoIcon>
              <MdEmail size={28} aria-hidden />
            </InfoIcon>
            <InfoText>
              <h3>{t('contact.email_info.title')}</h3>
              <p>
                <a href={`mailto:jhusef@me.com?subject=${mailSubject}`}>
                  {t('contact.email_info.value')}
                </a>
              </p>
            </InfoText>
          </InfoItem>

          <InfoItem>
            <InfoIcon>
              <MdChat size={28} aria-hidden />
            </InfoIcon>
            <InfoText>
              <h3>{t('contact.phone_info.title')}</h3>
              <p><a href="tel:+573234699010">{t('contact.phone_info.value')}</a></p>
            </InfoText>
          </InfoItem>

        </ContactInfo>

        <ContactActions variants={itemVariants}>
          <ActionButton href={`mailto:jhusef@me.com?subject=${mailSubject}`}>
            {t('contact.send')}
          </ActionButton>

          <SocialLinks>
            <SocialLinkButton href="https://github.com/jhusef-lopez" target="_blank" rel="noopener noreferrer">
              <FaGithub aria-hidden />
              <span>{t('contact.social.github')}</span>
            </SocialLinkButton>
            <SocialLinkButton href="https://www.linkedin.com/in/jhusef/" target="_blank" rel="noopener noreferrer">
              <FaLinkedin aria-hidden />
              <span>{t('contact.social.linkedin')}</span>
            </SocialLinkButton>
            <SocialLinkButton href="https://www.instagram.com/jhusef.dev/" target="_blank" rel="noopener noreferrer">
              <FaInstagram aria-hidden />
              <span>{t('contact.social.instagram')}</span>
            </SocialLinkButton>
          </SocialLinks>
        </ContactActions>
      </ContactLayout>
    </PageContainer>
  );
};

export default ContactPage;
